import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { Box } from '@components/Box';
import { Button } from '@components/Button';
import { Columns } from '@components/Columns';
import { Text } from '@components/typography';
import { VimeoVideo } from '@components/VimeoVideo';
import { getCreateWeddingUrl } from '@utils/urlHelpers.util';
import {
  SectionContainer,
  TextPairingContainer,
  ImageTextPairingContainer,
  BorderdCircle,
  StyledTextImageColumn,
  StyledTextPairFlex,
  StyledTextImageLeftColumn,
  VideoWrapper,
  StyledSectionHeader,
} from './HowItWorksSection.styles';
import { Flex } from '@components/Flex';

interface SectionProps {
  eyebrow: string;
  title: string;
  body: string;
  imageUrl: string;
  videoUrl: string;
}

interface HowItWorksSectionProps {
  howItWorksSectionData: {
    eyebrow: string;
    title: string;
    body: string;
    getStarted: string;
    section1: SectionProps;
    section2: SectionProps;
    section3: SectionProps;
  };
}

export const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({ howItWorksSectionData }) => {
  const { eyebrow, title, body, getStarted, section1, section2, section3 } = howItWorksSectionData;
  const sectionId = 'HowItWorksSection';
  return (
    <SectionContainer paddingBottom={[6, 10]} id={sectionId}>
      <StyledSectionHeader>
        <Columns centered>
          <Columns.Column width={12}>
            <StyledTextPairFlex flexDirection="column">
              <TextPairingContainer
                eyebrow={eyebrow}
                eyebrowColor="humanaDark4"
                eyebrowVariant={'sectionEyeBrow20'}
                eyebrowTagName="h3"
                alignment={'center'}
                title={title}
                titleVariant={'hed72Hero'}
                titleColor="neutra6"
                titleTagName="h2"
                body={ReactHtmlParser(body)}
                bodyColor="neutra5"
                bodyVariant="dek20"
              />
              <Box marginTop={[8]}>
                <Button
                  id="invitationsReimaginedGetStarted"
                  variant="secondary"
                  forwardedAs={'a'}
                  href={getCreateWeddingUrl('save-the-date')}
                >
                  {getStarted}
                </Button>
              </Box>
            </StyledTextPairFlex>
          </Columns.Column>
        </Columns>
      </StyledSectionHeader>
      <StyledTextImageColumn marginTop={[8, 10]} marginBottom={[6, 0]} verticalAlign="center" centered>
        <Columns.Column width={4}>
          <Flex alignItems={['center', 'flex-start']} flexDirection="column">
            <BorderdCircle marginTop={[7, 0]} marginBottom={[6, 4]}>
              <Text tagName="span" color="neutra3" variant="sectionEyeBrow24">
                {section1.eyebrow}
              </Text>
            </BorderdCircle>
            <ImageTextPairingContainer
              title={section1.title}
              titleVariant={'sectionEyeBrow20'}
              titleColor="neutra6"
              titleTagName="h3"
              body={section1.body}
              bodyColor="neutra5"
              bodyVariant="featureCopy17"
            />
          </Flex>
        </Columns.Column>
        <Columns.Column offset={1} width={4}>
          <VideoWrapper>
            <VimeoVideo
              posterUrl={section1.imageUrl}
              embedUrl={section1.videoUrl}
              title={section1.title}
              autoPlay
              background
              controls={false}
              autoPause={false}
              alt="create-invite"
              muted
              loop
              cover
              posterOptimization="aggressive"
            />
          </VideoWrapper>
        </Columns.Column>
      </StyledTextImageColumn>
      <StyledTextImageLeftColumn marginTop={[8]} verticalAlign="center" centered>
        <Columns.Column width={4}>
          <Flex alignItems={['center', 'flex-start']} flexDirection="column" style={{ width: '100%' }}>
            <VideoWrapper>
              <VimeoVideo
                posterUrl={section2.imageUrl}
                embedUrl={section2.videoUrl}
                title={section2.title}
                autoPlay
                background
                controls={false}
                autoPause={false}
                alt="guest-list"
                muted
                loop
                cover
                posterOptimization="aggressive"
              />
            </VideoWrapper>
          </Flex>
        </Columns.Column>
        <Columns.Column offset={1} width={4}>
          <Flex alignItems={['center', 'flex-start']} flexDirection="column">
            <BorderdCircle marginTop={[6, 0]} marginBottom={[4]}>
              <Text tagName="span" color="neutra3" variant="sectionEyeBrow24">
                {section2.eyebrow}
              </Text>
            </BorderdCircle>
            <ImageTextPairingContainer
              title={ReactHtmlParser(section2.title)}
              titleVariant={'sectionEyeBrow20'}
              titleColor="neutra6"
              titleTagName="h3"
              body={section2.body}
              bodyColor="neutra5"
              bodyVariant="featureCopy17"
            />
          </Flex>
        </Columns.Column>
      </StyledTextImageLeftColumn>
      <StyledTextImageColumn marginTop={[8]} verticalAlign="center" centered>
        <Columns.Column width={4}>
          <Flex alignItems={['center', 'flex-start']} flexDirection="column">
            <BorderdCircle marginTop={[6, 0]} marginBottom={[4]}>
              <Text tagName="span" color="neutra3" variant="sectionEyeBrow24">
                {section3.eyebrow}
              </Text>
            </BorderdCircle>
            <ImageTextPairingContainer
              title={section3.title}
              titleVariant={'sectionEyeBrow20'}
              titleColor="neutra6"
              titleTagName="h3"
              body={section3.body}
              bodyColor="neutra5"
              bodyVariant="featureCopy17"
            />
          </Flex>
        </Columns.Column>
        <Columns.Column offset={1} width={4}>
          <VideoWrapper>
            <VimeoVideo
              posterUrl={section3.imageUrl}
              embedUrl={section3.videoUrl}
              title={section3.title}
              autoPlay
              background
              controls={false}
              autoPause={false}
              alt="finally-rsvp"
              muted
              loop
              cover
              posterOptimization="aggressive"
            />
          </VideoWrapper>
        </Columns.Column>
      </StyledTextImageColumn>
    </SectionContainer>
  );
};
